import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Search,
  Filter,
  Star,
  MapPin,
  DollarSign,
  Monitor,
  Cpu,
  MemoryStick,
  HardDrive,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ShoppingCart,
  SlidersHorizontal,
  Globe,
  Zap,
  Key,
  Copy,
  Check,
  Trash2,
  Edit3,
  Lock,
} from 'lucide-react';
import {
  marketplaceApi,
  billingApi,
  MarketplaceListing,
  MarketplaceListingDetail,
  MarketplaceFilters,
  MarketplacePagination,
  MarketplaceSearchParams,
  ListingReview,
  GpuRental,
  GpuRentalResponse,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useAgentSync } from '../hooks/useAgentSync';
import clsx from 'clsx';

// Matches backend PREPAID_DISCOUNT_PERCENT
const PREPAID_DISCOUNT_PERCENT = 10;

// ─── Helpers ──────────────────────────────────────────────

function formatBytes(bytes: number) {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${bytes} B`;
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const px = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={clsx(px, i <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-foreground-muted')}
        />
      ))}
    </div>
  );
}

function LeaseStatusBadge({ isOccupied, agentStatus }: { isOccupied: boolean; agentStatus: string }) {
  const isOnline = agentStatus === 'ONLINE';
  const config = !isOnline
    ? { icon: XCircle, label: 'Offline', cls: 'text-foreground-muted bg-foreground-muted/10' }
    : isOccupied
      ? { icon: Lock, label: 'Leased', cls: 'text-yellow-400 bg-yellow-400/10' }
      : { icon: CheckCircle2, label: 'Free', cls: 'text-green-400 bg-green-400/10' };
  const Icon = config.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', config.cls)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded hover:bg-background-hover transition-colors" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-foreground-muted" />}
    </button>
  );
}

// ─── Filter Dropdown ──────────────────────────────────────

function FilterDropdown({
  label,
  icon: Icon,
  options,
  value,
  onChange,
  multiple = false,
}: {
  label: string;
  icon: typeof Globe;
  options: string[];
  value: string | string[];
  onChange: (val: string | string[]) => void;
  multiple?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const hasValue = selected.length > 0;

  const toggle = (opt: string) => {
    if (multiple) {
      const next = selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt];
      onChange(next);
    } else {
      onChange(opt === value ? '' : opt);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
          hasValue
            ? 'border-accent bg-accent/10 text-accent'
            : 'border-border bg-background-secondary text-foreground-secondary hover:bg-background-hover'
        )}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{hasValue ? `${label} (${selected.length})` : label}</span>
        <ChevronDown className={clsx('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && options.length > 0 && (
        <div className="absolute top-full left-0 mt-1 w-56 max-h-60 overflow-y-auto bg-background border border-border rounded-lg shadow-lg z-50">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm hover:bg-background-hover transition-colors flex items-center justify-between',
                selected.includes(opt) && 'text-accent bg-accent/5'
              )}
            >
              {opt}
              {selected.includes(opt) && <CheckCircle2 className="w-3.5 h-3.5 text-accent" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Listing Card ─────────────────────────────────────────

function ListingCard({
  listing,
  onClick,
}: {
  listing: MarketplaceListing;
  onClick: () => void;
}) {
  const hw = listing.agent.hardwareInfo;
  const gpu = hw?.gpus?.[0];

  return (
    <div
      onClick={onClick}
      className={clsx(
        'bg-background-secondary border rounded-lg p-5 hover:shadow-lg transition-all duration-200 cursor-pointer group',
        listing.isOccupied
          ? 'border-yellow-400/30 hover:border-yellow-400/50 hover:shadow-yellow-400/5'
          : 'border-border hover:border-accent/40 hover:shadow-accent/5'
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-foreground truncate group-hover:text-accent transition-colors">
            {listing.title}
          </h3>
          <p className="text-xs text-foreground-muted mt-0.5">
            by {listing.seller.displayName}
          </p>
        </div>
        <LeaseStatusBadge isOccupied={listing.isOccupied} agentStatus={listing.agent.status} />
      </div>

      {gpu && (
        <div className="flex items-center gap-2 mb-3 p-2.5 bg-background rounded-md border border-border/50">
          <Monitor className="w-4 h-4 text-accent shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{gpu.model}</p>
            <p className="text-xs text-foreground-muted">{formatBytes(gpu.vram)} VRAM</p>
          </div>
        </div>
      )}

      {hw && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-foreground-secondary">
          <div className="flex items-center gap-1.5">
            <Cpu className="w-3 h-3 text-foreground-muted" />
            <span className="truncate">{hw.cpu.brand}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MemoryStick className="w-3 h-3 text-foreground-muted" />
            <span>{formatBytes(hw.memory.total)} RAM</span>
          </div>
          {hw.disk && (
            <div className="flex items-center gap-1.5">
              <HardDrive className="w-3 h-3 text-foreground-muted" />
              <span>{formatBytes(hw.disk.total)} Disk</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-foreground-muted" />
            <span className="truncate">{listing.region}, {listing.country}</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-3 text-xs">
        <Zap className="w-3 h-3 text-foreground-muted" />
        <span className="text-foreground-muted">Reliability:</span>
        {listing.agent.reliabilityInfo ? (
          <span
            className={clsx(
              'font-medium',
              listing.agent.reliabilityInfo.overallState === 'reliable' && 'text-green-400',
              listing.agent.reliabilityInfo.overallState === 'degraded' && 'text-yellow-400',
              listing.agent.reliabilityInfo.overallState === 'unreliable' && 'text-error'
            )}
          >
            {listing.agent.reliabilityInfo.overallState}
          </span>
        ) : (
          <span className="text-foreground-muted/60">Not assessed</span>
        )}
      </div>

      {listing.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {listing.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-accent/10 text-accent text-[10px] font-medium rounded-full">
              {tag}
            </span>
          ))}
          {listing.tags.length > 3 && (
            <span className="px-2 py-0.5 text-foreground-muted text-[10px]">+{listing.tags.length - 3}</span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <StarRating rating={listing.avgRating} />
          <span className="text-xs text-foreground-muted">
            ({listing.reviewCount})
          </span>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 text-accent font-semibold">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="text-sm">{listing.pricePerHour.toFixed(2)}/hr</span>
          </div>
          <p className="text-[10px] text-green-400">
            From ${(Math.round(listing.pricePerHour * (1 - PREPAID_DISCOUNT_PERCENT / 100) * 100) / 100).toFixed(2)}/hr prepaid
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Review Card ──────────────────────────────────────────

function ReviewCard({
  review,
  isOwn,
  onEdit,
  onDelete,
}: {
  review: ListingReview;
  isOwn: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="p-4 bg-background rounded-lg border border-border/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center">
            <span className="text-[10px] font-medium text-accent">
              {review.reviewer.displayName[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <span className="text-sm font-medium text-foreground">{review.reviewer.displayName}</span>
        </div>
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} />
          {isOwn && (
            <div className="flex items-center gap-1 ml-2">
              <button onClick={onEdit} className="p-1 rounded hover:bg-background-hover text-foreground-muted hover:text-accent transition-colors" title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDelete} className="p-1 rounded hover:bg-background-hover text-foreground-muted hover:text-error transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      {review.comment && (
        <p className="text-sm text-foreground-secondary">{review.comment}</p>
      )}
      <p className="text-[11px] text-foreground-muted mt-2">
        {new Date(review.createdAt).toLocaleDateString()}
      </p>
    </div>
  );
}

// ─── Rent GPU Modal ───────────────────────────────────────

function RentGpuModal({
  listing,
  onClose,
  onSuccess,
}: {
  listing: MarketplaceListingDetail;
  onClose: () => void;
  onSuccess: (result: GpuRentalResponse) => void;
}) {
  const [mode, setMode] = useState<'open_ended' | 'prepaid_hours'>('open_ended');
  const [hours, setHours] = useState(1);
  const [balance, setBalance] = useState<number | null>(null);
  const [paygVerified, setPaygVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gstPercent, setGstPercent] = useState(0);
  const [platformFeePercent, setPlatformFeePercent] = useState(40);
  const rentInFlight = useRef(false);

  useEffect(() => {
    Promise.all([
      billingApi.getSubscriptionStatus(),
      billingApi.getRates(),
    ]).then(([sub, rates]) => {
      setBalance(sub.balance);
      setPaygVerified(sub.paygVerified || false);
      setGstPercent(rates.gstPercent ?? 0);
      setPlatformFeePercent(rates.platformFeePercent ?? 40);
      setIsLoadingBalance(false);
    }).catch(() => setIsLoadingBalance(false));
  }, []);

  const discountedRate = Math.round(listing.pricePerHour * (1 - PREPAID_DISCOUNT_PERCENT / 100) * 100) / 100;

  // --- Prepaid cost breakdown ---
  const gpuRentPrepaid = mode === 'prepaid_hours' ? Math.round(hours * discountedRate * 100) / 100 : 0;
  const totalWithoutDiscount = mode === 'prepaid_hours' ? Math.round(hours * listing.pricePerHour * 100) / 100 : 0;
  const savings = Math.round((totalWithoutDiscount - gpuRentPrepaid) * 100) / 100;
  const platformServiceFeePrepaid = Math.round(gpuRentPrepaid * (platformFeePercent / 100) * 100) / 100;
  const sellerEarningsPrepaid = Math.round((gpuRentPrepaid - platformServiceFeePrepaid) * 100) / 100;
  const gstAmountPrepaid = Math.round(gpuRentPrepaid * (gstPercent / 100) * 100) / 100;
  const totalBuyerCostPrepaid = Math.round((gpuRentPrepaid + gstAmountPrepaid) * 100) / 100;

  // --- Open-ended per-hour breakdown ---
  const platformServiceFeePerHourOE = Math.round(listing.pricePerHour * (platformFeePercent / 100) * 100) / 100;
  const sellerEarningsPerHourOE = Math.round((listing.pricePerHour - platformServiceFeePerHourOE) * 100) / 100;
  const gstPerHourOE = Math.round(listing.pricePerHour * (gstPercent / 100) * 100) / 100;
  const effectiveRateOE = Math.round((listing.pricePerHour + gstPerHourOE) * 100) / 100;

  // --- Balance check ---
  const minOpenEndedBalance = effectiveRateOE;
  const canAfford = balance !== null && (
    mode === 'open_ended'
      ? (balance >= minOpenEndedBalance || paygVerified)
      : balance >= totalBuyerCostPrepaid
  );

  const handleRent = async () => {
    if (rentInFlight.current) return;
    rentInFlight.current = true;
    setError(null);
    setIsLoading(true);
    try {
      const result = await marketplaceApi.rentGpu(listing.id, {
        mode,
        ...(mode === 'prepaid_hours' ? { hours } : {}),
      });
      onSuccess(result);
    } catch (err: any) {
      setError(err.message || 'Failed to rent GPU');
    } finally {
      setIsLoading(false);
      rentInFlight.current = false;
    }
  };

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content relative max-w-lg">
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-foreground mb-1">Rent GPU</h2>
        <p className="text-sm text-foreground-secondary mb-4">{listing.title} - ${listing.pricePerHour.toFixed(2)}/hr</p>

        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">{error}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Rental Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode('open_ended')}
                className={clsx(
                  'p-3 rounded-lg border text-left transition-colors',
                  mode === 'open_ended' ? 'border-accent bg-accent/10' : 'border-border hover:bg-background-hover'
                )}
              >
                <p className="text-sm font-medium text-foreground">Open-Ended</p>
                <p className="text-xs text-foreground-muted mt-0.5">Pay per hour until you release</p>
              </button>
              <button
                onClick={() => setMode('prepaid_hours')}
                className={clsx(
                  'p-3 rounded-lg border text-left transition-colors relative',
                  mode === 'prepaid_hours' ? 'border-accent bg-accent/10' : 'border-border hover:bg-background-hover'
                )}
              >
                <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                  {PREPAID_DISCOUNT_PERCENT}% OFF
                </span>
                <p className="text-sm font-medium text-foreground">Prepaid Hours</p>
                <p className="text-xs text-foreground-muted mt-0.5">
                  ${discountedRate.toFixed(2)}/hr{' '}
                  <span className="line-through text-foreground-muted/60">${listing.pricePerHour.toFixed(2)}</span>
                </p>
              </button>
            </div>
          </div>

          {mode === 'prepaid_hours' && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Number of Hours</label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
                className="input w-full"
              />
              <div className="mt-3 p-4 bg-background-secondary rounded-xl border border-border space-y-2">
                <p className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide mb-1">Cost Breakdown</p>
                <div className="flex justify-between text-xs text-foreground-secondary">
                  <span>GPU Rent ({hours}h x ${listing.pricePerHour.toFixed(2)}/hr)</span>
                  <span>${totalWithoutDiscount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-green-400">
                  <span>Prepaid Discount ({PREPAID_DISCOUNT_PERCENT}%)</span>
                  <span>-${savings.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-foreground-muted">
                  <span>Platform Service Fee ({platformFeePercent}%)</span>
                  <span>-${platformServiceFeePrepaid.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-foreground-secondary font-medium">
                  <span>GPU Cost (to seller)</span>
                  <span>${sellerEarningsPrepaid.toFixed(2)}</span>
                </div>
                {gstPercent > 0 && (
                  <div className="flex justify-between text-xs text-foreground-secondary">
                    <span>GST ({gstPercent}%)</span>
                    <span>+${gstAmountPrepaid.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-border pt-2 flex justify-between text-sm font-semibold">
                  <span className="text-foreground">Total You Pay</span>
                  <span className="text-accent">${totalBuyerCostPrepaid.toFixed(2)}</span>
                </div>
                {balance !== null && (
                  <div className="flex justify-between text-xs text-foreground-muted">
                    <span>Balance after purchase</span>
                    <span>${Math.max(0, balance - totalBuyerCostPrepaid).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'open_ended' && (
            <div className="p-4 bg-background-secondary rounded-xl border border-border space-y-2">
              <p className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide mb-1">Cost Breakdown (per hour)</p>
              <div className="flex justify-between text-xs text-foreground-secondary">
                <span>GPU Rent</span>
                <span>${listing.pricePerHour.toFixed(2)}/hr</span>
              </div>
              <div className="flex justify-between text-xs text-foreground-muted">
                <span>Platform Service Fee ({platformFeePercent}%)</span>
                <span>-${platformServiceFeePerHourOE.toFixed(2)}/hr</span>
              </div>
              <div className="flex justify-between text-xs text-foreground-secondary font-medium">
                <span>GPU Cost (to seller)</span>
                <span>${sellerEarningsPerHourOE.toFixed(2)}/hr</span>
              </div>
              {gstPercent > 0 && (
                <div className="flex justify-between text-xs text-foreground-secondary">
                  <span>GST ({gstPercent}%)</span>
                  <span>+${gstPerHourOE.toFixed(2)}/hr</span>
                </div>
              )}
              <div className="border-t border-border pt-2 flex justify-between text-sm font-semibold">
                <span className="text-foreground">Effective Rate</span>
                <span className="text-accent">${effectiveRateOE.toFixed(2)}/hr</span>
              </div>
              <p className="text-[10px] text-foreground-muted">Billed per second while the rental is active.</p>
            </div>
          )}

          <div className="p-3 bg-background-secondary rounded-lg border border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-secondary">Your Balance:</span>
              {isLoadingBalance ? (
                <Loader2 className="w-4 h-4 animate-spin text-foreground-muted" />
              ) : (
                <span className={clsx('font-semibold', canAfford ? 'text-green-400' : 'text-error')}>
                  ${balance?.toFixed(2) ?? '0.00'}
                </span>
              )}
            </div>
            {!canAfford && !isLoadingBalance && (
              <p className="text-xs text-error mt-1">
                {mode === 'prepaid_hours'
                  ? `Need $${totalBuyerCostPrepaid.toFixed(2)}${gstPercent > 0 ? ` (incl. $${gstAmountPrepaid.toFixed(2)} GST)` : ''} but only have $${balance?.toFixed(2)}`
                  : `Open-ended requires $${minOpenEndedBalance.toFixed(2)} balance (1hr${gstPercent > 0 ? ' + GST' : ''}) or PAYG verification. Go to Billing to add funds or set up PAYG.`}
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="btn btn-secondary flex-1">Cancel</button>
            <button
              onClick={handleRent}
              disabled={isLoading || !canAfford || isLoadingBalance}
              className="btn btn-primary flex-1"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
              {mode === 'prepaid_hours' ? `Rent for $${totalBuyerCostPrepaid.toFixed(2)}` : 'Start Rental'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── API Key Success Modal ────────────────────────────────

function ApiKeySuccessModal({
  result,
  onClose,
}: {
  result: GpuRentalResponse;
  onClose: () => void;
}) {
  const apiBaseUrl = window.location.origin;
  const [activeTab, setActiveTab] = useState<'simple' | 'llm'>('simple');

  const pythonSnippet = `import os
import requests
import urllib3
urllib3.disable_warnings(urllib3.exceptions.InsecureRequestWarning)

API_BASE_URL = os.getenv("API_BASE_URL", "${apiBaseUrl}")
API_KEY = os.getenv("API_KEY", "${result.apiKey.key}")

# Submit a compute task to your rented GPU
response = requests.post(
    f"{API_BASE_URL}/api/compute",
    headers={"Authorization": f"Bearer {API_KEY}"},
    json={
        "command": "python",
        "args": ["my_script.py"],
        "timeout": 300
    },
    verify=False  # Self-signed TLS cert
)
task = response.json()
print("Task submitted:", task)

if "error" in task:
    print(f"Error: {task['error']}")
    exit(1)

# Check task status
task_id = task["task"]["id"]
status_resp = requests.get(
    f"{API_BASE_URL}/api/compute/{task_id}",
    headers={"Authorization": f"Bearer {API_KEY}"},
    verify=False  # Self-signed TLS cert
)
print("Task status:", status_resp.json())`;

  const llmSnippet = `import requests
import time
import os
import sys
import urllib3
urllib3.disable_warnings(urllib3.exceptions.InsecureRequestWarning)

if sys.platform == 'win32':
    sys.stdout.reconfigure(encoding='utf-8')

# =============================================================================
# Configuration
# =============================================================================
API_BASE_URL = os.getenv("API_BASE_URL", "${apiBaseUrl}")
API_KEY = os.getenv("API_KEY", "${result.apiKey.key}")
REQUEST_TIMEOUT = int(os.getenv("REQUEST_TIMEOUT", "120"))

def get_headers():
    return {
        "Authorization": f"Bearer {API_KEY}",
        "Content-Type": "application/json",
    }

def test_connection():
    """Test basic connectivity to the compute API"""
    print("\\n" + "=" * 60)
    print("Testing API Connection")
    print("=" * 60)
    print(f"API URL: {API_BASE_URL}")
    print(f"API Key: {API_KEY[:20]}...")
    print("-" * 60)

    try:
        response = requests.get(
            f"{API_BASE_URL}/api/compute",
            headers=get_headers(),
            timeout=10,
            verify=False,
        )
        if response.status_code == 200:
            data = response.json()
            tasks = data.get("tasks", [])
            print(f"API authenticated successfully")
            print(f"  Existing tasks: {len(tasks)}")
            return True
        else:
            print(f"API returned status {response.status_code}")
            try:
                err = response.json()
                print(f"  Error: {err.get('error', err)}")
            except Exception:
                print(f"  Response: {response.text[:200]}")
            return False
    except requests.exceptions.RequestException as e:
        print(f"API request failed: {e}")
        return False

def submit_llm_task():
    """Submit GPT-2 Text Generation Task"""
    print("\\nSubmitting GPT-2 Text Generation Task...")

    # Assumes the seller has Docker mode enabled with a PyTorch image
    # (e.g. pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime) so
    # torch, transformers, and model weights are pre-installed.
    llm_command = """
python << 'PYEOF'
import torch
from transformers import GPT2LMHeadModel, GPT2Tokenizer

print("=" * 60)
print("GPT-2 Text Generation on GPU")
print("=" * 60)

print(f"CUDA Available: {torch.cuda.is_available()}")
if torch.cuda.is_available():
    print(f"GPU: {torch.cuda.get_device_name(0)}")
print(f"PyTorch Version: {torch.__version__}")
print()

print("Loading GPT-2 model...")
tokenizer = GPT2Tokenizer.from_pretrained('gpt2')
device = 'cuda' if torch.cuda.is_available() else 'cpu'
model = GPT2LMHeadModel.from_pretrained('gpt2').to(device)
model.eval()

print(f"Model loaded on {device}")
print(f"  Parameters: {sum(p.numel() for p in model.parameters()):,}")
print()

prompts = [
    "The future of artificial intelligence is",
    "In a world where robots",
    "Who is Bill Gates?"
]

print("Generating text...")
print("-" * 60)

for prompt in prompts:
    inputs = tokenizer.encode(prompt, return_tensors='pt').to(device)
    with torch.no_grad():
        outputs = model.generate(
            inputs,
            max_length=80,
            num_return_sequences=1,
            temperature=0.8,
            top_p=0.9,
            do_sample=True,
            pad_token_id=tokenizer.eos_token_id,
        )
    generated = tokenizer.decode(outputs[0], skip_special_tokens=True)
    print(f"Prompt: '{prompt}'")
    print(f"Generated: {generated}")
    print("-" * 60)

print()
print("LLM Text Generation Complete!")
print("=" * 60)
PYEOF
"""

    payload = {
        "command": "/bin/bash",
        "args": ["-c", llm_command.strip()],
        "timeout": 300,
    }

    try:
        response = requests.post(
            f"{API_BASE_URL}/api/compute",
            headers=get_headers(),
            json=payload,
            timeout=REQUEST_TIMEOUT,
            verify=False,
        )
        data = response.json()

        if "error" in data:
            print(f"API Error: {data['error']}")
            return None

        task_id = data["task"]["id"]
        print(f"Task submitted: {task_id}")
        return task_id
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def poll_task(task_id):
    """Poll task until it completes"""
    print("\\nRunning LLM task (may take 2-3 min for model download)...")

    max_wait = 180
    interval = 5

    for i in range(max_wait // interval):
        time.sleep(interval)
        elapsed = (i + 1) * interval
        try:
            resp = requests.get(
                f"{API_BASE_URL}/api/compute/{task_id}",
                headers=get_headers(),
                timeout=10,
                verify=False,
            )
            if resp.status_code != 200:
                print(f"  [{elapsed}s] Could not fetch status")
                continue

            task = resp.json().get("task", {})
            status = task.get("status", "unknown")
            print(f"  [{elapsed}s] Status: {status}")

            if status in ("completed", "failed", "cancelled"):
                return task
        except requests.exceptions.RequestException:
            print(f"  [{elapsed}s] Could not fetch status")

    print("  Timed out waiting for task")
    return None

def main():
    print("=" * 60)
    print("GPU LLM Demo - GPT-2 Text Generation")
    print("=" * 60)

    if not test_connection():
        print("\\nConnection failed. Make sure your rental is active and the API key is valid.")
        return

    print("\\n" + "=" * 60)
    print("Connection Verified - Starting LLM Task")
    print("=" * 60)

    task_id = submit_llm_task()
    if not task_id:
        print("Failed to submit task")
        return

    result = poll_task(task_id)
    if not result:
        print("Could not retrieve task results")
        return

    print("\\n" + "=" * 60)
    print("LLM OUTPUT:")
    print("=" * 60)
    stdout = result.get("stdout", "")
    stderr = result.get("stderr", "")
    if stdout:
        print(stdout)
    if stderr:
        print("\\n--- stderr ---")
        print(stderr)
    print("=" * 60)
    print("\\nComplete!")

if __name__ == "__main__":
    main()`;

  const activeSnippet = activeTab === 'simple' ? pythonSnippet : llmSnippet;

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content relative max-w-2xl max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-400/20 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">GPU Rented Successfully</h2>
            <p className="text-sm text-foreground-secondary">{result.rental.listingTitle}</p>
          </div>
        </div>

        <div className="mb-4 p-4 bg-yellow-400/10 border border-yellow-400/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-yellow-400" />
            <p className="text-sm font-semibold text-yellow-400">Save your API key now - it won't be shown again!</p>
          </div>
          <div className="flex items-center gap-2 bg-background rounded-md p-3 border border-border font-mono text-sm break-all">
            <span className="text-foreground flex-1">{result.apiKey.key}</span>
            <CopyButton text={result.apiKey.key} />
          </div>
        </div>

        <div className="mb-4 p-3 bg-background-secondary rounded-lg border border-border">
          <p className="text-xs font-medium text-foreground-secondary mb-1">Compute Endpoint</p>
          <div className="flex items-center gap-2">
            <code className="text-sm text-accent">{apiBaseUrl}/api/compute</code>
            <CopyButton text={`${apiBaseUrl}/api/compute`} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex gap-1 bg-background-secondary rounded-lg p-0.5 border border-border">
              <button
                onClick={() => setActiveTab('simple')}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'simple'
                    ? 'bg-accent text-white'
                    : 'text-foreground-secondary hover:text-foreground',
                )}
              >
                Simple
              </button>
              <button
                onClick={() => setActiveTab('llm')}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'llm'
                    ? 'bg-accent text-white'
                    : 'text-foreground-secondary hover:text-foreground',
                )}
              >
                LLM Demo
              </button>
            </div>
            <CopyButton text={activeSnippet} />
          </div>
          <pre className="bg-background rounded-lg border border-border p-4 text-xs text-foreground-secondary overflow-x-auto whitespace-pre-wrap">
            {activeSnippet}
          </pre>
        </div>
      </div>
    </div>
  );
}

// ─── Listing Detail Modal ─────────────────────────────────

function ListingDetailModal({
  listingId,
  onClose,
}: {
  listingId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [detail, setDetail] = useState<MarketplaceListingDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [editingReview, setEditingReview] = useState(false);
  const [showRentModal, setShowRentModal] = useState(false);
  const [rentResult, setRentResult] = useState<GpuRentalResponse | null>(null);

  const fetchDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const { listing } = await marketplaceApi.get(listingId);
      setDetail(listing);
    } catch (err: any) {
      setError(err.message || 'Failed to load listing');
    } finally {
      setIsLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setReviewError(null);
    setIsSubmittingReview(true);
    try {
      if (editingReview) {
        await marketplaceApi.updateReview(listingId, {
          rating: reviewRating,
          comment: reviewComment.trim(),
        });
        setEditingReview(false);
      } else {
        await marketplaceApi.submitReview(listingId, {
          rating: reviewRating,
          comment: reviewComment.trim(),
        });
      }
      await fetchDetail();
      setReviewComment('');
      setReviewRating(5);
    } catch (err: any) {
      setReviewError(err.message || 'Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleEditReview = (review: ListingReview) => {
    setReviewRating(review.rating);
    setReviewComment(review.comment);
    setEditingReview(true);
  };

  const handleDeleteReview = async () => {
    try {
      await marketplaceApi.deleteReview(listingId);
      await fetchDetail();
    } catch (err: any) {
      setReviewError(err.message || 'Failed to delete review');
    }
  };

  const hw = detail?.agent.hardwareInfo;
  const gpu = hw?.gpus?.[0];
  const isOwnListing = detail?.seller.id === user?.id;

  return (
    <>
      <div className="modal">
        <div className="modal-backdrop" onClick={onClose} />
        <div className="modal-content p-0 relative max-w-2xl max-h-[85vh] flex flex-col">
          <button onClick={onClose} className="absolute top-4 right-4 z-10 text-foreground-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-foreground-muted" />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-error">{error}</div>
          ) : detail ? (
            <div className="overflow-y-auto">
              {/* Header */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-xl font-bold text-foreground">{detail.title}</h2>
                    <p className="text-sm text-foreground-secondary mt-1">
                      by {detail.seller.displayName} &middot; Member since{' '}
                      {new Date(detail.seller.memberSince).toLocaleDateString()}
                    </p>
                  </div>
                  <LeaseStatusBadge isOccupied={detail.isOccupied} agentStatus={detail.agent.status} />
                </div>

                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5">
                    <StarRating rating={detail.avgRating} size="md" />
                    <span className="text-sm text-foreground-secondary">
                      {detail.avgRating.toFixed(1)} ({detail.reviewCount} reviews)
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-accent font-bold text-lg">
                      <DollarSign className="w-4 h-4" />
                      {detail.pricePerHour.toFixed(2)}/hr
                    </div>
                    <p className="text-[11px] text-green-400">
                      From ${(Math.round(detail.pricePerHour * (1 - PREPAID_DISCOUNT_PERCENT / 100) * 100) / 100).toFixed(2)}/hr with prepaid
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <MapPin className="w-4 h-4 text-foreground-muted" />
                  <span className="text-sm text-foreground-secondary">{detail.region}, {detail.country}</span>
                </div>

                {detail.description && (
                  <p className="text-sm text-foreground-secondary mt-3">{detail.description}</p>
                )}

                {detail.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {detail.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Rent Button */}
                {!isOwnListing && !detail.isOccupied && (
                  <div className="mt-4 space-y-1.5">
                    <button
                      onClick={() => setShowRentModal(true)}
                      className="btn btn-primary w-full"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Rent This GPU - ${detail.pricePerHour.toFixed(2)}/hr
                    </button>
                    <p className="text-center text-[11px] text-green-400">
                      Save {PREPAID_DISCOUNT_PERCENT}% with prepaid hours
                    </p>
                  </div>
                )}
                {detail.isOccupied && !isOwnListing && (
                  <div className="mt-4 p-3 bg-yellow-400/10 border border-yellow-400/20 rounded-lg text-sm text-yellow-400 text-center">
                    This GPU is currently rented by another user.
                  </div>
                )}
              </div>

              {/* Hardware */}
              <div className="p-6 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3">Hardware Specifications</h3>
                <div className="grid grid-cols-2 gap-4">
                  {gpu && (
                    <div className="flex items-start gap-2.5">
                      <Monitor className="w-4 h-4 text-accent mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{gpu.model}</p>
                        <p className="text-xs text-foreground-muted">
                          {formatBytes(gpu.vram)} VRAM
                          {gpu.temperatureGpu != null && ` · ${gpu.temperatureGpu}°C`}
                        </p>
                      </div>
                    </div>
                  )}
                  {hw && (
                    <>
                      <div className="flex items-start gap-2.5">
                        <Cpu className="w-4 h-4 text-accent mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-foreground">{hw.cpu.brand}</p>
                          <p className="text-xs text-foreground-muted">{hw.cpu.cores} cores · {hw.cpu.speed} GHz</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <MemoryStick className="w-4 h-4 text-accent mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-foreground">Memory</p>
                          <p className="text-xs text-foreground-muted">
                            {formatBytes(hw.memory.total)} total · {formatBytes(hw.memory.free)} free
                          </p>
                        </div>
                      </div>
                      {hw.disk && (
                        <div className="flex items-start gap-2.5">
                          <HardDrive className="w-4 h-4 text-accent mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-foreground">Disk</p>
                            <p className="text-xs text-foreground-muted">
                              {formatBytes(hw.disk.total)} total · {formatBytes(hw.disk.free)} free
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-4 flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-accent" />
                  <span className="text-foreground-secondary">Reliability:</span>
                  {detail.agent.reliabilityInfo ? (
                    <span
                      className={clsx(
                        'font-medium',
                        detail.agent.reliabilityInfo.overallState === 'reliable' && 'text-green-400',
                        detail.agent.reliabilityInfo.overallState === 'degraded' && 'text-yellow-400',
                        detail.agent.reliabilityInfo.overallState === 'unreliable' && 'text-error'
                      )}
                    >
                      {detail.agent.reliabilityInfo.overallState}
                      {detail.agent.reliabilityInfo.scores && (
                        <span className="text-foreground-muted font-normal ml-2 text-xs">
                          (uptime: {((detail.agent.reliabilityInfo.scores.uptime ?? 0) * 100).toFixed(0)}%
                          {' · '}latency: {((detail.agent.reliabilityInfo.scores.latency ?? 0) * 100).toFixed(0)}%)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-foreground-muted">Not yet assessed</span>
                  )}
                </div>
              </div>

              {/* Reviews */}
              <div className="p-6">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  Reviews ({detail.reviewCount})
                </h3>

                {/* Submit / Edit review */}
                {!isOwnListing && (!detail.userHasReviewed || editingReview) && (
                  <form onSubmit={handleSubmitReview} className="mb-4 p-4 bg-background-secondary rounded-lg border border-border">
                    <p className="text-sm font-medium text-foreground mb-2">
                      {editingReview ? 'Edit Your Review' : 'Write a Review'}
                    </p>
                    {reviewError && (
                      <div className="mb-2 p-2 bg-error/10 border border-error/20 rounded text-sm text-error">
                        {reviewError}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-foreground-secondary">Rating:</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setReviewRating(i)}
                            className="p-0.5"
                          >
                            <Star
                              className={clsx(
                                'w-5 h-5 transition-colors',
                                i <= reviewRating ? 'text-yellow-400 fill-yellow-400' : 'text-foreground-muted hover:text-yellow-300'
                              )}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      className="input w-full resize-none"
                      rows={3}
                      placeholder="Share your experience with this GPU..."
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      {editingReview && (
                        <button
                          type="button"
                          onClick={() => { setEditingReview(false); setReviewComment(''); setReviewRating(5); }}
                          className="btn btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      )}
                      <button type="submit" disabled={isSubmittingReview} className="btn btn-primary text-sm">
                        {isSubmittingReview ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Star className="w-3.5 h-3.5" />
                        )}
                        {editingReview ? 'Update Review' : 'Submit Review'}
                      </button>
                    </div>
                  </form>
                )}

                {detail.userHasReviewed && !isOwnListing && !editingReview && (
                  <div className="mb-4 p-3 bg-green-400/10 border border-green-400/20 rounded-lg text-sm text-green-400">
                    You have already reviewed this listing. You can edit or delete your review below.
                  </div>
                )}

                {detail.reviews.length > 0 ? (
                  <div className="space-y-3">
                    {detail.reviews.map((review) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        isOwn={review.reviewer.id === user?.id}
                        onEdit={() => handleEditReview(review)}
                        onDelete={handleDeleteReview}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-muted text-center py-6">
                    No reviews yet. Be the first to review!
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showRentModal && detail && (
        <RentGpuModal
          listing={detail}
          onClose={() => setShowRentModal(false)}
          onSuccess={(result) => {
            setShowRentModal(false);
            setRentResult(result);
          }}
        />
      )}

      {rentResult && (
        <ApiKeySuccessModal
          result={rentResult}
          onClose={() => {
            setRentResult(null);
            onClose();
          }}
        />
      )}
    </>
  );
}

// ─── My Rentals Panel ─────────────────────────────────────

function ActiveRentalCard({ rental, onRelease, releasingId, onExpired, platformFeePercent }: {
  rental: any;
  onRelease: (id: string) => void;
  releasingId: string | null;
  onExpired?: () => void;
  platformFeePercent: number;
}) {
  const [liveCost, setLiveCost] = useState(0);
  const [hoursElapsed, setHoursElapsed] = useState(0);
  const [livePlatformFee, setLivePlatformFee] = useState(0);
  const [liveSellerEarnings, setLiveSellerEarnings] = useState(0);
  const expiredTriggered = useRef(false);

  const isPrepaid = rental.mode === 'PREPAID_HOURS';
  const prepaidHours: number = rental.prepaidHours ?? 0;

  const startTime = new Date(rental.startedAt).getTime();
  const estimatedEndTime = isPrepaid && prepaidHours > 0
    ? new Date(startTime + prepaidHours * 3600000)
    : null;

  const hw = rental.listing?.agent?.hardwareInfo;
  const gpu = hw?.gpus?.[0];

  useEffect(() => {
    const ratePerSecond = rental.pricePerHour / 3600;
    const tick = () => {
      const elapsedSec = (Date.now() - startTime) / 1000;
      const elapsed = elapsedSec / 3600;
      setHoursElapsed(elapsed);
      const prepaidCost = (rental.prepaidHours ?? 0) * rental.pricePerHour;
      const cost = isPrepaid ? Math.round(prepaidCost * 100) / 100 : ratePerSecond * elapsedSec;
      setLiveCost(cost);

      const fee = Math.round(cost * (platformFeePercent / 100) * 10000) / 10000;
      setLivePlatformFee(fee);
      setLiveSellerEarnings(Math.round((cost - fee) * 10000) / 10000);

      if (isPrepaid && prepaidHours > 0 && elapsed >= prepaidHours && !expiredTriggered.current) {
        expiredTriggered.current = true;
        onExpired?.();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [rental, isPrepaid, prepaidHours, onExpired, startTime, platformFeePercent]);

  const remainingHours = isPrepaid ? Math.max(0, prepaidHours - hoursElapsed) : 0;
  const usagePercent = isPrepaid && prepaidHours > 0 ? Math.min(100, (hoursElapsed / prepaidHours) * 100) : 0;
  const progressColor = usagePercent > 85 ? 'bg-red-500' : usagePercent > 60 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="p-4 bg-background-secondary rounded-lg border border-accent/30">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-foreground">{rental.listingTitle}</p>
          <p className="text-xs text-foreground-muted">
            {isPrepaid ? `Prepaid ${prepaidHours}h` : 'Open-ended'}
            {isPrepaid && rental.discountPercent > 0 && (
              <span className="ml-1 px-1 py-0.5 bg-green-500/20 text-green-400 rounded text-[10px] font-bold">
                {rental.discountPercent}% OFF
              </span>
            )}
          </p>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-green-400 bg-green-400/10">
          <CheckCircle2 className="w-3 h-3" />
          Active
        </span>
      </div>

      {/* Start / Finish dates */}
      <div className="grid grid-cols-2 gap-3 mb-3 p-2.5 bg-background rounded-md border border-border/50 text-xs">
        <div>
          <p className="text-foreground-muted">Started</p>
          <p className="text-foreground font-medium">{new Date(rental.startedAt).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-foreground-muted">{isPrepaid ? 'Estimated End' : 'Ends'}</p>
          <p className="text-foreground font-medium">
            {estimatedEndTime ? estimatedEndTime.toLocaleString() : 'Until released'}
          </p>
        </div>
      </div>

      {/* GPU Hardware Details */}
      {hw && (
        <div className="flex flex-wrap items-center gap-3 mb-3 p-2.5 bg-background rounded-md border border-border/50 text-xs">
          {gpu && (
            <div className="flex items-center gap-1.5">
              <Monitor className="w-3.5 h-3.5 text-accent" />
              <span className="text-foreground font-medium">{gpu.model}</span>
              <span className="text-foreground-muted">({formatBytes(gpu.vram)} VRAM)</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-accent" />
            <span className="text-foreground-secondary">{hw.cpu.brand}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MemoryStick className="w-3.5 h-3.5 text-accent" />
            <span className="text-foreground-secondary">{formatBytes(hw.memory.total)} RAM</span>
          </div>
        </div>
      )}

      {isPrepaid && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-foreground-muted">{hoursElapsed.toFixed(2)}h of {prepaidHours}h used</span>
            <span className={clsx(
              'font-bold font-mono',
              usagePercent > 85 ? 'text-red-400' : usagePercent > 60 ? 'text-yellow-400' : 'text-green-400'
            )}>
              {remainingHours.toFixed(2)}h remaining
            </span>
          </div>
          <div className="w-full h-2 bg-foreground-muted/20 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all duration-1000', progressColor)}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
        {!isPrepaid && (
          <div>
            <p className="text-foreground-muted">Hours Used</p>
            <p className="text-foreground font-medium">{hoursElapsed.toFixed(2)}h</p>
          </div>
        )}
        <div>
          <p className="text-foreground-muted">Rate</p>
          <p className="text-foreground font-medium">
            ${rental.pricePerHour.toFixed(2)}/hr
            {isPrepaid && rental.listingPricePerHour && rental.listingPricePerHour !== rental.pricePerHour && (
              <span className="ml-1 line-through text-foreground-muted/60">${rental.listingPricePerHour.toFixed(2)}</span>
            )}
          </p>
        </div>
        <div>
          <p className="text-foreground-muted">Cost/sec</p>
          <p className="text-foreground font-medium">${(rental.pricePerHour / 3600).toFixed(6)}</p>
        </div>
        <div>
          <p className="text-foreground-muted">{isPrepaid ? 'Total Paid' : 'Live Cost'}</p>
          <p className="text-green-400 font-bold font-mono">${liveCost.toFixed(4)}</p>
        </div>
      </div>

      {/* Live Seller / Platform Fee Breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-3 p-2.5 bg-background rounded-md border border-border/50 text-xs">
        <div>
          <p className="text-foreground-muted">Seller Earnings</p>
          <p className="text-foreground font-medium font-mono">${liveSellerEarnings.toFixed(4)}</p>
        </div>
        <div>
          <p className="text-foreground-muted">Platform Fee ({platformFeePercent}%)</p>
          <p className="text-foreground-muted/80 font-medium font-mono">${livePlatformFee.toFixed(4)}</p>
        </div>
      </div>

      {rental.apiKeyPrefix && (
        <div className="flex items-center gap-2 mb-3 text-xs">
          <Key className="w-3 h-3 text-foreground-muted" />
          <span className="text-foreground-muted">API Key:</span>
          <code className="text-foreground">{rental.apiKeyPrefix}...</code>
        </div>
      )}
      <button
        onClick={() => onRelease(rental.id)}
        disabled={releasingId === rental.id}
        className="btn btn-secondary text-sm w-full"
      >
        {releasingId === rental.id ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <XCircle className="w-3.5 h-3.5" />
        )}
        Release GPU
      </button>
    </div>
  );
}

function MyRentalsPanel({ refreshKey }: { refreshKey: number }) {
  const [rentals, setRentals] = useState<GpuRental[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [platformFeePercent, setPlatformFeePercent] = useState(40);

  const fetchRentals = useCallback(async () => {
    setIsLoading(true);
    try {
      const { rentals: data } = await marketplaceApi.getRentals();
      setRentals(data);
    } catch {
      // Silently fail - panel is optional
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
    billingApi.getRates().then((rates) => {
      setPlatformFeePercent(rates.platformFeePercent ?? 40);
    }).catch(() => {});
  }, [fetchRentals, refreshKey]);

  const handleRelease = async (rentalId: string) => {
    setReleasingId(rentalId);
    try {
      await marketplaceApi.releaseRental(rentalId);
      await fetchRentals();
    } catch {
      // Error handled silently
    } finally {
      setReleasingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-foreground-muted" />
      </div>
    );
  }

  if (rentals.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-foreground-muted">You have no rentals yet.</p>
      </div>
    );
  }

  const activeRentals = rentals.filter((r) => r.status === 'ACTIVE');
  const pastRentals = rentals.filter((r) => r.status !== 'ACTIVE');

  return (
    <div className="space-y-4">
      {activeRentals.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Active Rentals</h3>
          <div className="space-y-3">
            {activeRentals.map((rental) => (
              <ActiveRentalCard
                key={rental.id}
                rental={rental}
                onRelease={handleRelease}
                releasingId={releasingId}
                onExpired={fetchRentals}
                platformFeePercent={platformFeePercent}
              />
            ))}
          </div>
        </div>
      )}

      {pastRentals.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Past Rentals</h3>
          <div className="space-y-2">
            {pastRentals.map((rental) => {
              const isPrepaid = rental.mode === 'PREPAID_HOURS';
              const displayCost = isPrepaid && rental.prepaidHours
                ? Math.round(rental.prepaidHours * rental.pricePerHour * 100) / 100
                : rental.totalCostUsd;
              const platformFee = isPrepaid && rental.prepaidHours
                ? Math.round(displayCost * (platformFeePercent / 100) * 100) / 100
                : rental.platformFeeUsd;
              const sellerEarned = isPrepaid && rental.prepaidHours
                ? Math.round((displayCost - platformFee) * 100) / 100
                : (rental.sellerEarningsUsd ?? (rental.totalCostUsd - rental.platformFeeUsd));

              const hw = rental.listing?.agent?.hardwareInfo;
              const gpu = hw?.gpus?.[0];

              return (
                <div key={rental.id} className="p-4 bg-background-secondary rounded-lg border border-border">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{rental.listingTitle}</p>
                      <p className="text-xs text-foreground-muted">
                        {isPrepaid && rental.prepaidHours ? `${rental.prepaidHours}h prepaid` : `${rental.hoursUsed.toFixed(2)}h`}
                        {rental.discountPercent > 0 && (
                          <span className="ml-1 text-green-400">({rental.discountPercent}% off)</span>
                        )}
                      </p>
                    </div>
                    <span className={clsx(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      rental.status === 'COMPLETED' ? 'text-foreground-muted bg-foreground-muted/10' : 'text-error bg-error/10'
                    )}>
                      {rental.status}
                    </span>
                  </div>

                  {/* Start / Finish dates */}
                  <div className="grid grid-cols-2 gap-3 mb-2 text-xs">
                    <div>
                      <p className="text-foreground-muted">Started</p>
                      <p className="text-foreground">{new Date(rental.startedAt).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-foreground-muted">Ended</p>
                      <p className="text-foreground">
                        {rental.endedAt ? new Date(rental.endedAt).toLocaleString() : '—'}
                      </p>
                    </div>
                  </div>

                  {/* GPU Hardware Details */}
                  {hw && (
                    <div className="flex flex-wrap items-center gap-3 mb-2 text-xs">
                      {gpu && (
                        <div className="flex items-center gap-1.5">
                          <Monitor className="w-3 h-3 text-accent" />
                          <span className="text-foreground">{gpu.model}</span>
                          <span className="text-foreground-muted">({formatBytes(gpu.vram)})</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Cpu className="w-3 h-3 text-accent" />
                        <span className="text-foreground-secondary">{hw.cpu.brand}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MemoryStick className="w-3 h-3 text-accent" />
                        <span className="text-foreground-secondary">{formatBytes(hw.memory.total)}</span>
                      </div>
                    </div>
                  )}

                  {/* Cost / Fee Breakdown */}
                  <div className="grid grid-cols-3 gap-3 text-xs pt-2 border-t border-border/50">
                    <div>
                      <p className="text-foreground-muted">Total Paid</p>
                      <p className="text-foreground font-medium">${displayCost.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-muted">Seller Earned</p>
                      <p className="text-foreground font-medium">${sellerEarned.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-muted">Platform Fee ({platformFeePercent}%)</p>
                      <p className="text-foreground-muted/80 font-medium">${platformFee.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────

export function BuyGpuPage() {
  const [activeTab, setActiveTab] = useState<'browse' | 'rentals'>('browse');
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [pagination, setPagination] = useState<MarketplacePagination | null>(null);
  const [filters, setFilters] = useState<MarketplaceFilters | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [rentalsRefreshKey, setRentalsRefreshKey] = useState(0);
  const [expiredBanner, setExpiredBanner] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedGpuModels, setSelectedGpuModels] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minRating, setMinRating] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [freeOnly, setFreeOnly] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    marketplaceApi.getFilters().then(setFilters).catch(() => {});
  }, []);

  const fetchListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: MarketplaceSearchParams = {
        page: String(page),
        limit: '18',
        sortBy,
        sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedRegions.length) params.region = selectedRegions.join(',');
      if (selectedCountries.length) params.country = selectedCountries.join(',');
      if (selectedGpuModels.length) params.gpuModel = selectedGpuModels[0];
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      if (minRating) params.minRating = minRating;
      if (onlineOnly) params.status = 'ONLINE';

      const result = await marketplaceApi.list(params);
      const filtered = freeOnly
        ? result.listings.filter((l) => !l.isOccupied)
        : result.listings;
      setListings(filtered);
      setPagination(result.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setIsLoading(false);
    }
  }, [page, sortBy, sortOrder, debouncedSearch, selectedRegions, selectedCountries, selectedGpuModels, minPrice, maxPrice, minRating, onlineOnly, freeOnly]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  useAgentSync({
    onAgentUpdate: useCallback(() => {
      fetchListings();
    }, [fetchListings]),
    onAgentOffline: useCallback(() => {
      fetchListings();
    }, [fetchListings]),
    onRentalExpired: useCallback((_rentalId: string, reason: string) => {
      setRentalsRefreshKey((k) => k + 1);
      const msg = reason === 'prepaid_hours_exhausted'
        ? 'A prepaid rental has expired — prepaid hours exhausted.'
        : 'A rental has expired — insufficient balance.';
      setExpiredBanner(msg);
      setTimeout(() => setExpiredBanner(null), 8000);
    }, []),
    onRentalReleased: useCallback(() => {
      fetchListings();
    }, [fetchListings]),
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedRegions, selectedCountries, selectedGpuModels, minPrice, maxPrice, minRating, onlineOnly, freeOnly, sortBy, sortOrder]);

  const activeFilterCount = [
    selectedRegions.length > 0,
    selectedCountries.length > 0,
    selectedGpuModels.length > 0,
    !!minPrice,
    !!maxPrice,
    !!minRating,
    onlineOnly,
    freeOnly,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSelectedGpuModels([]);
    setMinPrice('');
    setMaxPrice('');
    setMinRating('');
    setOnlineOnly(false);
    setFreeOnly(false);
    setSortBy('createdAt');
    setSortOrder('desc');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Buy Local GPU</h1>
          <p className="text-foreground-secondary mt-1">
            Browse and rent GPU compute from providers around the world.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-background-secondary rounded-lg p-1 border border-border w-fit">
        <button
          onClick={() => setActiveTab('browse')}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'browse' ? 'bg-accent text-white' : 'text-foreground-secondary hover:text-foreground'
          )}
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Browse Marketplace
          </div>
        </button>
        <button
          onClick={() => setActiveTab('rentals')}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'rentals' ? 'bg-accent text-white' : 'text-foreground-secondary hover:text-foreground'
          )}
        >
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            My Rentals
          </div>
        </button>
      </div>

      {expiredBanner && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {expiredBanner}
          <button onClick={() => setExpiredBanner(null)} className="ml-auto hover:text-yellow-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {activeTab === 'rentals' ? (
        <MyRentalsPanel refreshKey={rentalsRefreshKey} />
      ) : (
        <>
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-foreground-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input w-full pl-10 pr-4 py-2.5 text-sm"
              placeholder="Search by GPU model, provider name, tags..."
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Toggle + Sort */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
                  showFilters
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border bg-background-secondary text-foreground-secondary hover:bg-background-hover'
                )}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-accent text-white rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-foreground-muted hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-muted">Sort by:</span>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [s, o] = e.target.value.split('-');
                  setSortBy(s);
                  setSortOrder(o);
                }}
                className="input text-sm py-1.5 px-2 pr-8"
              >
                <option value="createdAt-desc">Newest</option>
                <option value="createdAt-asc">Oldest</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="rating-desc">Highest Rated</option>
                <option value="reviewCount-desc">Most Reviews</option>
              </select>
            </div>
          </div>

          {/* Filter Bar */}
          {showFilters && filters && (
            <div className="flex flex-wrap items-center gap-2 p-4 bg-background-secondary rounded-lg border border-border">
              <FilterDropdown
                label="Region"
                icon={Globe}
                options={filters.regions}
                value={selectedRegions}
                onChange={(v) => setSelectedRegions(v as string[])}
                multiple
              />
              <FilterDropdown
                label="Country"
                icon={MapPin}
                options={filters.countries}
                value={selectedCountries}
                onChange={(v) => setSelectedCountries(v as string[])}
                multiple
              />
              <FilterDropdown
                label="GPU Model"
                icon={Monitor}
                options={filters.gpuModels}
                value={selectedGpuModels}
                onChange={(v) => setSelectedGpuModels(v as string[])}
                multiple
              />

              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-foreground-muted" />
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="input w-20 text-sm py-1.5 px-2"
                  placeholder="Min"
                  min="0"
                  step="0.01"
                />
                <span className="text-foreground-muted text-xs">–</span>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="input w-20 text-sm py-1.5 px-2"
                  placeholder="Max"
                  min="0"
                  step="0.01"
                />
                <span className="text-xs text-foreground-muted">/hr</span>
              </div>

              <div className="flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 text-foreground-muted" />
                <select
                  value={minRating}
                  onChange={(e) => setMinRating(e.target.value)}
                  className="input text-sm py-1.5 px-2 pr-7"
                >
                  <option value="">Any Rating</option>
                  <option value="4">4+ Stars</option>
                  <option value="3">3+ Stars</option>
                  <option value="2">2+ Stars</option>
                  <option value="1">1+ Stars</option>
                </select>
              </div>

              <label className="flex items-center gap-1.5 cursor-pointer text-sm text-foreground-secondary">
                <input
                  type="checkbox"
                  checked={onlineOnly}
                  onChange={(e) => setOnlineOnly(e.target.checked)}
                  className="rounded border-border"
                />
                Online only
              </label>

              <label className="flex items-center gap-1.5 cursor-pointer text-sm text-foreground-secondary">
                <input
                  type="checkbox"
                  checked={freeOnly}
                  onChange={(e) => setFreeOnly(e.target.checked)}
                  className="rounded border-border"
                />
                Free only
              </label>
            </div>
          )}

          {/* Active filter pills */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedRegions.map((r) => (
                <span key={`r-${r}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  {r}
                  <button onClick={() => setSelectedRegions((prev) => prev.filter((v) => v !== r))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedCountries.map((c) => (
                <span key={`c-${c}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  {c}
                  <button onClick={() => setSelectedCountries((prev) => prev.filter((v) => v !== c))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedGpuModels.map((m) => (
                <span key={`g-${m}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  {m}
                  <button onClick={() => setSelectedGpuModels((prev) => prev.filter((v) => v !== m))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {minPrice && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  Min ${minPrice}/hr
                  <button onClick={() => setMinPrice('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {maxPrice && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  Max ${maxPrice}/hr
                  <button onClick={() => setMaxPrice('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {minRating && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                  {minRating}+ Stars
                  <button onClick={() => setMinRating('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {onlineOnly && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-400/10 text-green-400 text-xs font-medium rounded-full">
                  Online Only
                  <button onClick={() => setOnlineOnly(false)}><X className="w-3 h-3" /></button>
                </span>
              )}
              {freeOnly && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-400/10 text-green-400 text-xs font-medium rounded-full">
                  Free Only
                  <button onClick={() => setFreeOnly(false)}><X className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}

          {/* Results info */}
          {pagination && !isLoading && (
            <div className="flex items-center justify-between text-sm text-foreground-muted">
              <span>
                Showing {listings.length} of {pagination.total} listings
              </span>
              {pagination.totalPages > 1 && (
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">{error}</div>
          )}

          {/* Listing Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-foreground-muted" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20 space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto">
                <ShoppingCart className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">No listings found</h3>
                <p className="text-foreground-secondary text-sm mt-1 max-w-md mx-auto">
                  {activeFilterCount > 0 || debouncedSearch
                    ? 'Try adjusting your filters or search terms to find more GPUs.'
                    : 'No GPUs are currently listed on the marketplace. Check back later!'}
                </p>
              </div>
              {(activeFilterCount > 0 || debouncedSearch) && (
                <button onClick={clearAllFilters} className="btn btn-secondary mx-auto">
                  <Filter className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {listings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  onClick={() => setSelectedListing(listing.id)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-secondary text-sm py-1.5 px-3"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 3) {
                    pageNum = pagination.totalPages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={clsx(
                        'w-8 h-8 text-sm rounded-md transition-colors',
                        pageNum === page
                          ? 'bg-accent text-white font-medium'
                          : 'text-foreground-secondary hover:bg-background-hover'
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                className="btn btn-secondary text-sm py-1.5 px-3"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedListing && (
        <ListingDetailModal
          listingId={selectedListing}
          onClose={() => setSelectedListing(null)}
        />
      )}
    </div>
  );
}
